import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';

const HiveShareFAQ = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <Helmet>
        <title>HiveShare FAQ - Frequently Asked Questions</title>
        <meta name="description" content="Get answers to frequently asked questions about HiveShare - the video sharing app for students and young people." />
        <meta name="keywords" content="HiveShare FAQ, video sharing app, student videos, help" />
      </Helmet>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">HiveShare FAQ</h1>
          <p className="text-xl text-gray-600">Everything you need to know about HiveShare</p>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {/* Getting Started */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              1. Getting Started
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What is HiveShare?</h3>
                <p className="text-gray-700 leading-relaxed">
                  HiveShare is a video sharing app that lets you securely save, view, and share your favorite video memories. Whether it's camp activities, school events, sports highlights, or personal moments, HiveShare keeps your special memories organized and accessible.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Do I need an invitation to use HiveShare?</h3>
                <p className="text-gray-700 leading-relaxed">
                  No! HiveShare is open to everyone. You can sign up directly with your email or Apple ID and start saving videos right away.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Who can use HiveShare?</h3>
                <p className="text-gray-700 leading-relaxed">
                  HiveShare is designed for young people and students who want to save and share video memories. Anyone can create an account and start using the app.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I link to my organization, club, or team?</h3>
                <p className="text-gray-700 leading-relaxed">
                  You can connect to your organization using a HiveCode - a special code provided by your school, camp, sports team, or club. Go to Profile → Enter HiveCode and type in the code your organization gave you. Once connected, you'll automatically receive videos from your organization's events and activities.
                  <span className="inline-block ml-2 bg-yellow-100 text-yellow-800 text-sm px-2 py-1 rounded-md">Coming Soon!</span>
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How many videos can I save on the HiveShare free plan?</h3>
                <p className="text-gray-700 leading-relaxed">
                  On the free plan, you can save up to 25 videos at a time in HiveShare. Videos are automatically removed after 60 days from when they were uploaded. To keep videos permanently, download them to your Photos app before they expire. There's no limit on downloads to your device.
                </p>
              </div>
            </div>
          </section>

          {/* Viewing Videos */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              2. Viewing Videos
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I find videos?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Browse through your saved videos on the home screen. You can search by title, sort by date or favorites, and quickly find the moments you're looking for.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Why can't I see certain videos?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Videos are shared based on privacy settings and who appears in them. You'll only see videos that include you or are marked for everyone. If you think you're missing videos, contact your organization's administrator.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What does the heart/favorite button do?</h3>
                <p className="text-gray-700 leading-relaxed">
                  The heart button lets you mark videos as favorites. Tap the heart to turn it red and save a video to your favorites. This makes it easy to find your most treasured moments later.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I sort videos by favorites?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Tap the "Sort" button and select "Favorites first" from the menu. This will show all your favorited videos at the top of the list, followed by other videos sorted by newest first.
                </p>
              </div>
            </div>
          </section>

          {/* Downloading Videos */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              3. Downloading Videos
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Where do downloaded videos go?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Downloaded videos are saved directly to your phone's Photos app. Look for them in your Camera Roll or Videos album, just like videos you record yourself.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Why can't I download some videos?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Some organizations may disable downloads for certain videos due to privacy policies. If you see a video without a download button, it means the organization has restricted downloading for that content.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Do downloaded videos expire?</h3>
                <p className="text-gray-700 leading-relaxed">
                  No, once downloaded to your Photos app, videos are yours to keep forever. They won't expire or disappear, even if they're removed from HiveShare after 60 days.
                </p>
              </div>
            </div>
          </section>

          {/* Account Management */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              4. Account Management
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I update my account information?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Go to your Profile tab to view your account details. For email or password changes, use the account settings options.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I change my password?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Go to Profile → Account Settings → Change Password. Follow the prompts to update your password.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I delete my account?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Go to Profile → Delete Account. This will permanently remove your account and access to all saved videos. Videos you've already downloaded will remain in your Photos app.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">I forgot my password - what do I do?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Use the "Forgot Password" link on the login screen to reset your password via email.
                </p>
              </div>
            </div>
          </section>

          {/* Technical Issues */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              5. Technical Issues
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">The app won't load my videos</h3>
                <p className="text-gray-700 leading-relaxed mb-3">Try these steps:</p>
                <ol className="list-decimal list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
                  <li>Check your internet connection</li>
                  <li>Pull down on the video list to refresh</li>
                  <li>Close and reopen the app</li>
                  <li>Sign out and sign back in</li>
                </ol>
                <p className="text-gray-700 leading-relaxed mt-3">If problems persist, contact support.</p>
              </div>
            </div>
          </section>

          {/* Privacy & Security */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              6. Privacy & Security
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Who can see my videos?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Your videos are private to you. Only you can see the videos in your HiveShare library unless you choose to share them.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Is my data secure?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Yes, HiveShare uses bank-level encryption to protect your data. All videos are stored securely and transmitted over encrypted connections. We never sell or share your personal information.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How does HiveShare protect my privacy?</h3>
                <p className="text-gray-700 leading-relaxed mb-3">We use strict privacy controls:</p>
                <ul className="list-disc list-inside text-gray-700 leading-relaxed space-y-2 ml-4">
                  <li>Private video libraries</li>
                  <li>Secure authentication</li>
                  <li>Encrypted data storage</li>
                  <li>No public profiles</li>
                  <li>You control your content</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Organizations & Families */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              7. Organizations & Families
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How does my organization share videos?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Your organization's staff upload videos to HiveShare and tag them with the appropriate people. Videos are automatically organized and made available to the right people based on these tags.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">What happens if I leave the organization?</h3>
                <p className="text-gray-700 leading-relaxed">
                  When your enrollment ends, you'll lose access to new videos. However, any videos you've already downloaded to your Photos app are yours to keep forever.
                </p>
              </div>
            </div>
          </section>

          {/* Contact & Support */}
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-blue-600 mb-6 pb-3 border-b-2 border-gray-100">
              8. Contact & Support
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I get help?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Tap Profile → Contact Support to send us a text message. Include your account email and describe your issue, and we'll respond within 24 hours.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I report a problem?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Use the Contact Support button in your Profile tab. Send us details about the problem, including what you were trying to do and any error messages you saw.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Who do I contact for billing questions?</h3>
                <p className="text-gray-700 leading-relaxed">
                  Billing is handled by your organization. Contact your organization's office directly for questions about payments, fees, or billing schedules.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How do I suggest new features?</h3>
                <p className="text-gray-700 leading-relaxed">
                  We love hearing from users! Use Contact Support to share your ideas for making HiveShare even better. Your feedback helps us improve the app for everyone.
                </p>
              </div>

              <div className="text-center mt-8">
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={() => window.location.href = 'sms:+17706377525'}
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  Contact Support
                </Button>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 pt-8 border-t border-gray-200">
          <p className="text-gray-500 text-sm">© 2024 Hivetivity. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default HiveShareFAQ;